import * as React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import { useSession, useToastError } from '@hooks';
import { useUpdateUserMutation, GenderEnum } from '@graphql';

export function Gender() {
  const { user } = useSession();
  const { t } = useTranslation('common');
  const [isEditing, setIsEditing] = React.useState(false);
  const toastError = useToastError();

  const [updateUser] = useUpdateUserMutation();

  const formik = useFormik({
    initialValues: { gender: user?.gender ?? null },
    validationSchema: yup.object({
      gender: yup.string().oneOf(Object.values(GenderEnum)).nullable(),
    }),
    onSubmit: (values) => {
      updateUser({
        variables: {
          input: values,
        },
        onCompleted() {
          formik.resetForm({ values: { gender: values.gender } });
          setIsEditing(false);
        },
        onError: toastError,
      });
    },
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (!user) {
    return null;
  }

  return (
    <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
      <Stack width="100%" spacing={isEditing ? 1 : 0}>
        <Stack direction="row" width="100%" justifyContent="space-between">
          <Typography fontWeight="bold">{t('gender.label', { ns: 'field' })}</Typography>
          <Stack spacing={1} direction="row">
            {isEditing ? (
              <>
                <Button variant="contained" color="uncolored" size="small" onClick={handleCancel}>
                  {t('cancel', { ns: 'cta' })}
                </Button>
                <Button
                  disabled={!formik.dirty || !formik.isValid}
                  variant="contained"
                  color="success"
                  size="small"
                  type="submit"
                >
                  {t('validate', { ns: 'cta' })}
                </Button>
              </>
            ) : (
              <Button variant="contained" color="uncolored" size="small" onClick={handleEdit}>
                {t('edit', { ns: 'cta' })}
              </Button>
            )}
          </Stack>
        </Stack>
        <Stack>
          {isEditing ? (
            <Select
              {...formik.getFieldProps('gender')}
              fullWidth
              labelId="gender-select-label"
              name="gender"
              autoComplete="sex"
              label={t('gender.label')}
            >
              <MenuItem value="">
                <em>{t('word.select', { ns: 'common' })}</em>
              </MenuItem>
              <MenuItem value={GenderEnum.Masculine}>{t('gender.man', { ns: 'common' })}</MenuItem>
              <MenuItem value={GenderEnum.Feminine}>{t('gender.woman', { ns: 'common' })}</MenuItem>
              <MenuItem value={GenderEnum.NonBinary}>{t('gender.nonBinary', { ns: 'common' })}</MenuItem>
              <MenuItem value={GenderEnum.GenderFluid}>{t('gender.genderFluid', { ns: 'common' })}</MenuItem>
              <MenuItem value={GenderEnum.PreferNotToSay}>
                {t('gender.preferNotToSay', { ns: 'common' })}
              </MenuItem>
              <MenuItem value={GenderEnum.Other}>{t('gender.other', { ns: 'common' })}</MenuItem>
            </Select>
          ) : (
            <Typography color="text.secondary">{t(`gender.${user.gender}`)}</Typography>
          )}
        </Stack>
      </Stack>
    </form>
  );
}
